/**
 * WorkspaceSidebar — sidebar điều hướng trái dùng chung cho 3 workspace.
 *
 * - Thu gọn / mở rộng (desktop) → chỉ hiện icon + tooltip.
 * - Mobile: trượt vào từ trái, có overlay.
 * - Highlight mục active theo route hiện tại.
 * - Footer: user profile + credits + menu "..." (Hỗ trợ / Đánh giá).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, HelpCircle, Star,
  MoreHorizontal, MessageCircle, Phone, Mail, Send, Check, Loader2,
  FolderOpen, Settings as SettingsIcon, Sparkles, History, Share2, Repeat, LogOut,
} from 'lucide-react';
import type { WorkspaceMode, WorkspaceNavItem } from '../types';
import { WORKSPACE_BRAND, WORKSPACE_NAV, classifyTeacherStage } from '../data/navConfig';
import { useWorkspace } from '../context/WorkspaceProvider';
import { WorkspaceIcon } from './WorkspaceIcon';
import { UI_CONFIG, CONTACT_CONFIG } from '../../../config';
import { submitFeedback } from '../../../services/apiService';
import { View } from '../../../types';
import NotificationBell from '../../../components/common/NotificationBell';
import { formatCreditBalanceHeadline, type AiTier } from '../../../utils/creditCapacity';

export interface SidebarUserProfile {
  name: string;
  credits?: number;
  /** Cấp AI hiện tại — dùng làm tiêu đề thay số credit khi pricing_display_mode = 'plan'. */
  tier?: AiTier;
  /** @deprecated không còn dùng — formatCreditBalanceHeadline() tự đọc UI_CONFIG.CREDIT_LABEL. */
  creditLabel?: string;
  avatarUrl?: string;
  /** Hiển thị cảnh báo credits thấp (màu vàng). */
  lowCredits?: boolean;
}

interface WorkspaceSidebarProps {
  mode: WorkspaceMode;
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onNavigate: (path: string) => void;
  /** Thông tin user hiển thị cuối sidebar. */
  userProfile?: SidebarUserProfile;
  /** Slot tùy biến cuối sidebar (ví dụ box nâng cấp Premium). */
  footerSlot?: React.ReactNode;

  // ── Menu tài khoản (gộp từ dropdown avatar của WorkspaceHeader) ──
  /** Điều hướng theo View enum (Thư viện, Cài đặt, Nâng cấp…). */
  onNavigateView?: (view: View, payload?: any) => void;
  /** Đăng xuất. */
  onLogout?: () => void;
  /** Đổi vai trò GV ↔ PH. */
  onSwitchRole?: (role: 'teacher' | 'parent') => void;
  /** Cấu hình vai trò để biết có cho đổi vai trò / là affiliate hay không. */
  activeRoleConfig?: any;
  /** User đăng nhập bằng mã học sinh → ẩn các mục thanh toán. */
  isStudentCode?: boolean;
  /** User có affiliate đang bật. */
  isAffiliateActive?: boolean;
  /** Danh sách thông báo (chuyển từ header xuống). */
  notifications?: any[];
  onMarkAsRead?: (id: string) => void;
  onMarkAllAsRead?: () => void;
  onClearRead?: () => void;
  /** Toggle AI Panel (chuyển từ header xuống). */
  onToggleAIPanel?: () => void;
  isAIPanelOpen?: boolean;
}

export const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  mode,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
  onNavigate,
  userProfile,
  footerSlot,
  onNavigateView,
  onLogout,
  onSwitchRole,
  activeRoleConfig,
  isStudentCode,
  isAffiliateActive,
  notifications = [],
  onMarkAsRead,
  onMarkAllAsRead,
  onClearRead,
  onToggleAIPanel,
  isAIPanelOpen,
}) => {
  const location = useLocation();
  const brand = WORKSPACE_BRAND[mode];
  const { activeGradeLevel } = useWorkspace();

  // Cấp học hiện tại (chỉ áp cho teacher) → lọc nghiệp vụ phù hợp theo cấp.
  const stage = useMemo(
    () => (mode === 'teacher' ? classifyTeacherStage(activeGradeLevel) : null),
    [mode, activeGradeLevel],
  );

  // Lọc mục theo cấp học: mục không khai báo gradeStages → luôn hiện;
  // chưa xác định được cấp → hiện đủ (an toàn); section rỗng sau lọc → ẩn.
  const sections = useMemo(() => {
    const visible = (it: WorkspaceNavItem): boolean =>
      !it.gradeStages || !stage || it.gradeStages.includes(stage);
    const filterItems = (items: WorkspaceNavItem[]): WorkspaceNavItem[] =>
      items.filter(visible).map((it) =>
        it.children ? { ...it, children: filterItems(it.children) } : it,
      );
    return WORKSPACE_NAV[mode]
      .map((sec) => ({ ...sec, items: filterItems(sec.items) }))
      .filter((sec) => sec.items.length > 0);
  }, [mode, stage]);

  // Panel đang mở (Hỗ trợ / Đánh giá)
  const [panel, setPanel] = useState<'none' | 'support' | 'feedback'>('none');
  // Dropdown "..." trong footer user row
  const [footerMenuOpen, setFooterMenuOpen] = useState(false);
  const footerMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (footerMenuRef.current && !footerMenuRef.current.contains(e.target as Node)) {
        setFooterMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Active = nav item có path+search khớp DÀI NHẤT với route hiện tại.
  const activeKey = useMemo(() => {
    const fullPath = location.pathname + location.search;
    const pathname = location.pathname;
    let best: { key: string; len: number } | null = null;
    const visit = (it: WorkspaceNavItem) => {
      if (!it.path) return;
      // Exact match kể cả search params (dài nhất thắng)
      const [itPath, itSearch] = it.path.split('?');
      const itFull = it.path;
      const exactFull = fullPath === itFull || fullPath.startsWith(itFull + '&');
      const prefixPath = !itSearch && (pathname === itPath || pathname.startsWith(itPath + '/'));
      if ((exactFull || prefixPath) && (!best || itFull.length > best!.len)) {
        best = { key: it.key, len: itFull.length };
      }
      it.children?.forEach(visit);
    };
    sections.forEach((sec) => sec.items.forEach(visit));
    return best?.key ?? null;
  }, [location.pathname, location.search, sections]);

  const isActive = (item: WorkspaceNavItem) => item.key === activeKey;
  const isParentActive = (item: WorkspaceNavItem) => !!item.children?.some((c) => c.key === activeKey);

  // Nhóm cấp 2 đang mở. Tự mở nhóm chứa mục đang active.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  useEffect(() => {
    const parent = sections.flatMap((s) => s.items).find((it) => it.children?.some((c) => c.key === activeKey));
    if (parent) setExpandedKeys((prev) => (prev.has(parent.key) ? prev : new Set(prev).add(parent.key)));
  }, [activeKey, sections]);

  // ── Smart accordion: tự mở section từ trên xuống theo chiều cao nav có sẵn ──
  const ITEM_H = 40;   // chiều cao 1 nav item (py-2.5 + text ≈ 40px)
  const SEC_H  = 32;   // chiều cao accordion header (py-1.5 + text ≈ 32px)
  const GAP_H  = 6;    // khoảng cách giữa các section

  const sectionKeyOf = (title: string) => `__sec__${title}`;
  const navRef = useRef<HTMLElement>(null);
  const [navHeight, setNavHeight] = useState(0);

  // Đo chiều cao vùng nav bằng ResizeObserver.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setNavHeight(el.clientHeight));
    ro.observe(el);
    setNavHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  // Tính toán auto-expand: ưu tiên từ trên xuống cho đến khi hết chiều cao.
  // Section chứa mục active luôn được mở dù có thiếu chiều cao.
  const autoExpanded = useMemo<Set<string>>(() => {
    if (navHeight === 0) return new Set();

    const titledSections = sections.filter((s) => s.title);
    const homeSection    = sections.find((s) => !s.title);

    // Trừ đi không gian cố định: home items + tất cả accordion headers
    let remaining = navHeight;
    if (homeSection) remaining -= homeSection.items.length * ITEM_H + GAP_H;
    remaining -= titledSections.length * (SEC_H + GAP_H);

    // Section đang chứa activeKey luôn được ưu tiên mở trước
    const activeSectionTitle = titledSections.find((s) =>
      s.items.some((it) => it.key === activeKey || it.children?.some((c) => c.key === activeKey))
    )?.title;

    const result = new Set<string>();

    if (activeSectionTitle) {
      const sec = titledSections.find((s) => s.title === activeSectionTitle)!;
      const cost = sec.items.reduce((h, it) => h + ITEM_H + (it.children?.length ?? 0) * ITEM_H, 0);
      result.add(sectionKeyOf(activeSectionTitle));
      remaining -= cost;
    }

    // Điền từ trên xuống: mở section nếu còn đủ chỗ
    for (const sec of titledSections) {
      if (sec.title === activeSectionTitle) continue;
      const cost = sec.items.reduce((h, it) => h + ITEM_H + (it.children?.length ?? 0) * ITEM_H, 0);
      if (remaining >= cost) {
        result.add(sectionKeyOf(sec.title!));
        remaining -= cost;
      }
    }

    return result;
  }, [navHeight, sections, activeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual override: user click để đóng/mở thủ công (ghi đè auto).
  // Tự reset khi navHeight thay đổi đáng kể (resize màn hình) → để auto tính lại.
  const [manualOverrides, setManualOverrides] = useState<Map<string, boolean>>(new Map);
  const prevNavH = useRef(0);
  useEffect(() => {
    if (Math.abs(navHeight - prevNavH.current) > 60) {
      setManualOverrides(new Map());
      prevNavH.current = navHeight;
    }
  }, [navHeight]);

  const isSectionExpanded = (title: string): boolean => {
    const k = sectionKeyOf(title);
    return manualOverrides.has(k) ? manualOverrides.get(k)! : autoExpanded.has(k);
  };

  const toggleSection = (title: string) => {
    const k = sectionKeyOf(title);
    const current = isSectionExpanded(title);
    setManualOverrides((prev) => new Map(prev).set(k, !current));
  };

  const toggleExpand = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const handleClick = (item: WorkspaceNavItem) => {
    onCloseMobile();
    onNavigate(item.path);
  };

  // ── Render 1 mục lá (không có con) ──────────────────────────────────────
  const renderLeaf = (item: WorkspaceNavItem, opts: { child?: boolean; flyout?: boolean } = {}) => {
    const { child = false, flyout = false } = opts;
    const showLabel = flyout || !collapsed;
    const active = isActive(item);
    return (
      <button
        key={item.key}
        onClick={() => !item.comingSoon && handleClick(item)}
        disabled={item.comingSoon}
        title={collapsed && !flyout ? item.label : item.comingSoon ? 'Sắp ra mắt' : undefined}
        className={[
          'group relative w-full flex items-center rounded-lg font-medium transition-colors',
          // Mục con (2 cấp) nhỏ & gọn hơn để phân biệt cấp và bớt rối.
          collapsed && !flyout ? 'justify-center gap-3 px-3 py-2.5 text-sm' : child ? 'gap-2.5 px-3 py-1.5 text-[13px]' : 'gap-3 px-3 py-2.5 text-sm',
          active ? 'text-indigo-600 bg-indigo-50/60 font-semibold' : 'text-slate-600 hover:bg-slate-50',
          item.comingSoon ? 'opacity-60 cursor-not-allowed' : '',
        ].join(' ')}
      >
        <WorkspaceIcon
          name={item.icon}
          className={`${child && showLabel ? 'w-4 h-4' : 'w-5 h-5'} shrink-0 ${active ? 'text-indigo-500' : 'text-slate-400'}`}
          strokeWidth={2}
        />
        {showLabel && <span className="truncate flex-1 text-left">{item.label}</span>}
        {showLabel && item.comingSoon && (
          <span className="ml-auto bg-slate-100 text-slate-400 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0">Sắp có</span>
        )}
        {showLabel && item.badge && !item.comingSoon && (
          <span className="ml-auto bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0">{item.badge}</span>
        )}
        {collapsed && !flyout && (
          <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
            {item.label}
          </span>
        )}
      </button>
    );
  };

  // ── Render 1 nhóm cấp 2 (có children) ───────────────────────────────────
  const renderParent = (item: WorkspaceNavItem) => {
    const parentActive = isParentActive(item);

    if (collapsed) {
      return (
        <button
          key={item.key}
          onClick={() => handleClick(item)}
          title={item.label}
          className={[
            'group relative w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-colors',
            parentActive ? 'text-indigo-600 bg-indigo-50/60' : 'text-slate-600 hover:bg-slate-50',
          ].join(' ')}
        >
          <WorkspaceIcon name={item.icon} className={`w-5 h-5 ${parentActive ? 'text-indigo-500' : 'text-slate-400'}`} strokeWidth={2} />
          <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
            {item.label}
          </span>
        </button>
      );
    }

    const open = expandedKeys.has(item.key);
    return (
      <div key={item.key}>
        <button
          onClick={() => toggleExpand(item.key)}
          className={[
            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
            parentActive ? 'text-indigo-600 font-semibold' : 'text-slate-600 hover:bg-slate-50',
          ].join(' ')}
        >
          <WorkspaceIcon name={item.icon} className={`w-5 h-5 shrink-0 ${parentActive ? 'text-indigo-500' : 'text-slate-400'}`} strokeWidth={2} />
          <span className="truncate flex-1 text-left">{item.label}</span>
          {item.badge && (
            <span className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase shrink-0">{item.badge}</span>
          )}
          <ChevronRight className={`w-4 h-4 shrink-0 text-slate-300 transition-transform ${open ? 'rotate-90' : ''}`} />
        </button>
        {open && (
          <div className="mt-0.5 ml-[1.35rem] pl-2 border-l border-slate-100 space-y-0.5">
            {item.children!.map((c) => renderLeaf(c, { child: true }))}
          </div>
        )}
      </div>
    );
  };

  const widthClass = collapsed ? 'w-[76px]' : 'w-64';

  // ── Credits label ─────────────────────────────────────────────────────────
  // Dùng chung formatCreditBalanceHeadline() với Header (desktop chip + menu avatar) —
  // cùng 1 quyết định isPlanMode, không tự viết lại điều kiện ở đây.
  const creditsText = userProfile?.credits !== undefined
    ? formatCreditBalanceHeadline(userProfile.credits, userProfile.tier)
    : null;

  const avatarChar = userProfile?.name
    ? userProfile.name.trim().split(/\s+/).pop()?.charAt(0).toUpperCase() ?? 'U'
    : 'U';

  // ── Menu tài khoản (gộp từ dropdown avatar header) ──────────────────────
  const goView = (view: View, payload?: any) => {
    setFooterMenuOpen(false);
    onCloseMobile();
    onNavigateView?.(view, payload);
  };

  const renderAccountMenu = () => (
    <div className="w-56 max-h-[70vh] overflow-y-auto bg-white rounded-xl shadow-xl ring-1 ring-black/5 py-1">
      <button onClick={() => goView(View.DocumentLibrary)} className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
        <FolderOpen className="w-4 h-4 text-slate-400 shrink-0" /> Thư viện của tôi
      </button>
      {onSwitchRole && activeRoleConfig?.canSwitchRole && (
        <button
          onClick={() => { const t = activeRoleConfig.isRoleSwitched ? 'teacher' : 'parent'; setFooterMenuOpen(false); onSwitchRole(t); }}
          className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 border-y border-slate-50 my-1"
        >
          <Repeat className="w-4 h-4 shrink-0" /> {activeRoleConfig.isRoleSwitched ? 'Về vai trò Giáo viên' : 'Sang vai trò Phụ huynh'}
        </button>
      )}
      <button onClick={() => goView(View.Settings)} className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
        <SettingsIcon className="w-4 h-4 text-slate-400 shrink-0" /> Cài đặt
      </button>
      {!isStudentCode && (
        <>
          <button onClick={() => goView(View.CreditStore)} className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Sparkles className="w-4 h-4 text-slate-400 shrink-0" /> Nâng cấp tài khoản
          </button>
          <button onClick={() => goView(View.TransactionHistory)} className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <History className="w-4 h-4 text-slate-400 shrink-0" /> Lịch sử sử dụng AI
          </button>
          <button onClick={() => goView(View.AffiliateDashboard)} className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
            <Share2 className="w-4 h-4 text-slate-400 shrink-0" /> {isAffiliateActive ? 'Trung tâm Affiliate' : 'Bật kiếm tiền'}
          </button>
        </>
      )}
      <div className="border-t border-slate-100 my-1" />
      <button
        onClick={() => { setFooterMenuOpen(false); setPanel('support'); }}
        className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
      >
        <HelpCircle className="w-4 h-4 text-slate-400 shrink-0" /> Hỗ trợ & Phản hồi
      </button>
      <button
        onClick={() => { setFooterMenuOpen(false); setPanel('feedback'); }}
        className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-amber-50 hover:text-amber-600"
      >
        <Star className="w-4 h-4 text-slate-400 shrink-0" /> Đánh giá ứng dụng
      </button>
      {onLogout && (
        <>
          <div className="border-t border-slate-100 my-1" />
          <button onClick={() => { setFooterMenuOpen(false); onLogout(); }} className="flex items-center gap-2.5 w-full text-left px-3 py-2 text-sm text-rose-600 hover:bg-rose-50">
            <LogOut className="w-4 h-4 shrink-0" /> Đăng xuất
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      {/* Overlay mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-30 md:hidden"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          'relative bg-white border-r border-slate-200 flex flex-col shrink-0 z-40',
          'transition-all duration-300 h-full',
          widthClass,
          'fixed inset-y-0 left-0 md:static',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        ].join(' ')}
      >
        {/* Floating collapse button — chỉ desktop */}
        <button
          onClick={onToggleCollapse}
          className="hidden md:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-50 w-7 h-7 bg-white border border-slate-200 rounded-full shadow-md items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-300 hover:shadow-indigo-100 hover:shadow-lg transition-all duration-200"
          title={collapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
        >
          {collapsed
            ? <ChevronRight className="w-3.5 h-3.5" />
            : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>

        {/* Logo */}
        <div className="p-4 flex items-center justify-between border-b border-slate-50">
          <div className="flex items-center gap-3 overflow-hidden">
            {UI_CONFIG.APP_LOGO_URL ? (
              <img
                src={UI_CONFIG.APP_LOGO_URL}
                alt={UI_CONFIG.APP_NAME}
                className="w-10 h-10 rounded-xl object-contain shrink-0 shadow-lg shadow-indigo-100"
              />
            ) : (
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg shadow-indigo-100 shrink-0">
                {(UI_CONFIG.APP_NAME || 'B').charAt(0).toUpperCase()}
              </div>
            )}
            {!collapsed && (
              <div className="leading-none min-w-0">
                <h1 className="font-bold text-slate-900 text-base truncate">{UI_CONFIG.APP_NAME || 'BookAI'}</h1>
                <span className="text-[11px] text-slate-500 font-medium truncate block">{brand.subtitle}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* Chuông thông báo — đặt cạnh logo (logo trái, chuông phải) */}
            {!collapsed && notifications !== undefined && (
              <NotificationBell
                notifications={notifications}
                onNavigate={onNavigateView ?? (() => {})}
                onMarkAsRead={onMarkAsRead}
                onMarkAllAsRead={onMarkAllAsRead}
                onClearRead={onClearRead}
                align="left"
              />
            )}
            <button
              onClick={onCloseMobile}
              className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-slate-600"
              title="Đóng menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav ref={navRef} className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          {sections.map((section, si) => {
            const hasTitle = !!section.title;
            // collapsed: always render items (icon-only); expanded: respect accordion state
            const isOpen = !hasTitle || collapsed || isSectionExpanded(section.title!);

            return (
              <div key={si}>
                {/* ── Accordion header (section có title) ── */}
                {hasTitle && !collapsed && (
                  <button
                    onClick={() => toggleSection(section.title!)}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors group mb-0.5"
                  >
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider group-hover:text-slate-500 transition-colors">
                      {section.title}
                    </span>
                    <ChevronRight
                      className={`w-3 h-3 text-slate-300 group-hover:text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                    />
                  </button>
                )}

                {/* Divider mỏng khi thu gọn */}
                {hasTitle && collapsed && si > 0 && (
                  <div className="mx-3 border-t border-slate-100 my-1" />
                )}

                {/* Items — hiện khi: không có title (home group) HOẶC section đang mở */}
                {isOpen && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => (item.children ? renderParent(item) : renderLeaf(item)))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer slot (premium upsell, v.v.) */}
        {footerSlot && !collapsed && (
          <div className="px-3 pb-2">{footerSlot}</div>
        )}

        {/* ── User Profile Footer ─────────────────────────────────────────── */}
        <div className="border-t border-slate-100 p-2 shrink-0 space-y-1">

          {/* Nút Trợ lý AI — chuyển từ header xuống */}
          {onToggleAIPanel && !isAIPanelOpen && (
            collapsed ? (
              <button
                onClick={onToggleAIPanel}
                title="Trợ lý AI"
                className="group relative w-full flex items-center justify-center px-3 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
              >
                <Sparkles className="w-5 h-5" />
                <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  Trợ lý AI
                </span>
              </button>
            ) : (
              <button
                onClick={onToggleAIPanel}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-colors shadow-sm shadow-indigo-200"
              >
                <Sparkles className="w-4 h-4" /> Trợ lý AI
              </button>
            )
          )}

          {userProfile ? (
            <div className="relative" ref={footerMenuRef}>
              <div className={`flex items-center gap-1.5 ${collapsed ? 'flex-col' : ''}`}>
                {/* Bell chỉ còn ở đây khi sidebar thu gọn — bản mở rộng đã chuyển lên khu logo (đầu sidebar) */}
                {collapsed && notifications !== undefined && (
                  <div className="w-full flex justify-center">
                    <NotificationBell
                      notifications={notifications}
                      onNavigate={onNavigateView ?? (() => {})}
                      onMarkAsRead={onMarkAsRead}
                      onMarkAllAsRead={onMarkAllAsRead}
                      onClearRead={onClearRead}
                    />
                  </div>
                )}

                {/* Avatar + tên + credits + menu "..." */}
                <button
                  onClick={() => setFooterMenuOpen((v) => !v)}
                  title={collapsed ? userProfile.name : 'Tài khoản & tùy chọn'}
                  className={`flex-1 flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50 transition-colors min-w-0 ${collapsed ? 'w-full justify-center' : ''}`}
                >
                  {userProfile.avatarUrl ? (
                    <img src={userProfile.avatarUrl} alt={userProfile.name} className="w-7 h-7 rounded-full object-cover ring-2 ring-indigo-100 shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                      {avatarChar}
                    </div>
                  )}
                  {!collapsed && (
                    <>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="text-xs font-bold text-slate-700 truncate leading-tight">{userProfile.name}</p>
                        {creditsText && (
                          <p className={`text-[10px] font-semibold leading-tight mt-0.5 ${userProfile.lowCredits ? 'text-amber-500' : 'text-emerald-500'}`}>
                            {userProfile.lowCredits ? '⚠ ' : '✦ '}{creditsText}
                          </p>
                        )}
                      </div>
                      <MoreHorizontal className="w-4 h-4 text-slate-400 shrink-0" />
                    </>
                  )}
                </button>
              </div>
              {footerMenuOpen && (
                <div className={`absolute z-50 ${collapsed ? 'left-full bottom-0 ml-2' : 'bottom-full right-0 mb-1'}`}>
                  {renderAccountMenu()}
                </div>
              )}
            </div>
          ) : (
            /* Fallback khi chưa có user: chỉ hiện 2 nút gốc */
            <div className="space-y-0.5">
              <button
                onClick={() => setPanel('support')}
                title={collapsed ? 'Hỗ trợ & Phản hồi' : undefined}
                className={[
                  'group relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors text-sm font-medium',
                  collapsed ? 'justify-center' : '',
                ].join(' ')}
              >
                <HelpCircle className="w-4 h-4 shrink-0 text-slate-400" />
                {!collapsed && <span>Hỗ trợ & Phản hồi</span>}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                    Hỗ trợ & Phản hồi
                  </span>
                )}
              </button>
              <button
                onClick={() => setPanel('feedback')}
                title={collapsed ? 'Đánh giá ứng dụng' : undefined}
                className={[
                  'group relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-500 hover:bg-amber-50 hover:text-amber-600 transition-colors text-sm font-medium',
                  collapsed ? 'justify-center' : '',
                ].join(' ')}
              >
                <Star className="w-4 h-4 shrink-0 text-slate-400 group-hover:text-amber-500" />
                {!collapsed && <span>Đánh giá ứng dụng</span>}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                    Đánh giá ứng dụng
                  </span>
                )}
              </button>
            </div>
          )}
        </div>
      </aside>

      {panel !== 'none' && (
        <SidebarUtilityModal kind={panel} onClose={() => setPanel('none')} />
      )}
    </>
  );
};

// ── Modal Hỗ trợ / Đánh giá ─────────────────────────────────────────────────
const SidebarUtilityModal: React.FC<{
  kind: 'support' | 'feedback';
  onClose: () => void;
}> = ({ kind, onClose }) => {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === 'support' ? <SupportPanel onClose={onClose} /> : <FeedbackPanel onClose={onClose} />}
      </div>
    </div>,
    document.body,
  );
};

const SupportPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const channels = [
    CONTACT_CONFIG.MESSENGER_LINK && { label: 'Messenger', sub: 'Chat nhanh với hỗ trợ', href: CONTACT_CONFIG.MESSENGER_LINK, icon: MessageCircle, color: 'bg-blue-50 text-blue-600' },
    CONTACT_CONFIG.ZALO_LINK && { label: 'Zalo', sub: 'Nhắn tin qua Zalo', href: CONTACT_CONFIG.ZALO_LINK, icon: MessageCircle, color: 'bg-teal-50 text-teal-600' },
    CONTACT_CONFIG.HOTLINE && { label: 'Hotline', sub: String(CONTACT_CONFIG.HOTLINE), href: `tel:${String(CONTACT_CONFIG.HOTLINE).replace(/\s/g, '')}`, icon: Phone, color: 'bg-amber-50 text-amber-600' },
    CONTACT_CONFIG.EMAIL && { label: 'Email', sub: String(CONTACT_CONFIG.EMAIL), href: `mailto:${CONTACT_CONFIG.EMAIL}`, icon: Mail, color: 'bg-rose-50 text-rose-600' },
  ].filter(Boolean) as { label: string; sub: string; href: string; icon: React.FC<{ className?: string }>; color: string }[];

  return (
    <>
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-sm">Hỗ trợ & Phản hồi</h3>
            <p className="text-[11px] text-slate-400">Chúng tôi luôn sẵn sàng giúp bạn</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-4 space-y-2">
        {channels.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.label}
              href={c.href}
              target={c.href.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50 transition-all group"
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-700">{c.label}</p>
                <p className="text-[11px] text-slate-400 truncate">{c.sub}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0" />
            </a>
          );
        })}
      </div>
    </>
  );
};

const FeedbackPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (rating === 0 && !comment.trim()) {
      setError('Vui lòng chọn số sao hoặc nhập góp ý.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitFeedback(rating, comment.trim(), 'deep-workspace');
      if (res.success) {
        setDone(true);
        setTimeout(onClose, 1800);
      } else {
        setError(res.message || 'Không thể gửi đánh giá. Vui lòng thử lại.');
      }
    } catch (e: any) {
      setError(e?.message || 'Không thể kết nối máy chủ.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center">
            <Star className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800 text-sm">Đánh giá ứng dụng</h3>
            <p className="text-[11px] text-slate-400">Góp ý giúp BooKAI tốt hơn mỗi ngày</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>

      {done ? (
        <div className="px-5 py-10 text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3">
            <Check className="w-8 h-8" />
          </div>
          <p className="font-bold text-slate-800">Cảm ơn bạn!</p>
          <p className="text-xs text-slate-500 mt-1">Đánh giá của bạn đã được ghi nhận.</p>
        </div>
      ) : (
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-2.5 bg-rose-50 border border-rose-100 rounded-lg text-xs text-rose-600">{error}</div>
          )}
          <div className="flex justify-center gap-1.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" onClick={() => setRating(s)} className="transition-transform hover:scale-110">
                <Star className={`w-9 h-9 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            placeholder="Chia sẻ cảm nhận hoặc ý tưởng của bạn…"
            className="w-full p-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-50 resize-none"
          />
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-sm transition-colors shadow-sm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? 'Đang gửi…' : 'Gửi đánh giá'}
          </button>
        </div>
      )}
    </>
  );
};
